import api from './api'

/**
 * Authentication service
 * Handles all auth-related API calls including 2FA flow
 */
const authService = {
  /**
   * Login Step 1: Verify email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{session_token: string, requires_2fa: boolean, user_email: string}>}
   */
  loginStep1: async (email, password) => {
    const response = await api.post('/auth/login/step1/', { email, password })
    return response.data
  },

  /**
   * Login Step 2: Verify 2FA code
   * @param {string} sessionToken
   * @param {string} code
   * @param {boolean} isBackupCode
   * @returns {Promise<{access: string, refresh: string, user: object, backup_codes_remaining: number}>}
   */
  loginStep2: async (sessionToken, code, isBackupCode = false) => {
    const response = await api.post('/auth/login/step2/', {
      session_token: sessionToken,
      code,
      is_backup_code: isBackupCode,
    })
    return response.data
  },

  /**
   * Logout
   */
  logout: async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await api.post('/auth/logout/', { refresh: refreshToken })
      } catch (error) {
        // Ignore errors (token might already be blacklisted or invalid)
        // We still want to clear local storage
        console.warn('Logout API call failed, but clearing local tokens:', error.message)
      }
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  },

  /**
   * Get current user info
   * @returns {Promise<object>}
   */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me/')
    return response.data
  },

  /**
   * Get 2FA status
   * @returns {Promise<{is_enabled: boolean, backup_codes_remaining: number}>}
   */
  get2FAStatus: async () => {
    const response = await api.get('/auth/2fa/status/')
    return response.data
  },

  /**
   * Regenerate backup codes
   * @param {string} totpCode - TOTP code for verification
   * @returns {Promise<{backup_codes: string[]}>}
   */
  regenerateBackupCodes: async (totpCode) => {
    const response = await api.post('/auth/2fa/regenerate-backup-codes/', {
      code: totpCode,
    })
    return response.data
  },

  /**
   * Validate invitation token
   * @param {string} token
   * @returns {Promise<{email: string, expires_at: string}>}
   */
  validateInvitation: async (token) => {
    const response = await api.get(`/invitations/validate/${token}/`)
    return response.data
  },

  /**
   * Accept invitation Step 1: Submit user details and get QR code
   * @param {object} data - {token, password, password_confirm, first_name, last_name, username}
   * @returns {Promise<{qr_code: string, secret: string, session_token: string, email: string}>}
   */
  acceptInvitationStep1: async (data) => {
    const response = await api.post('/invitations/accept/step1/', data)
    return response.data
  },

  /**
   * Accept invitation Step 2: Verify TOTP code and create account
   * @param {string} sessionToken
   * @param {string} code
   * @returns {Promise<{user: object, tokens: {access: string, refresh: string}, backup_codes: string[]}>}
   */
  acceptInvitationStep2: async (sessionToken, code) => {
    const response = await api.post('/invitations/accept/step2/', {
      session_token: sessionToken,
      code,
    })
    return response.data
  },
}

export default authService
